import React, { useState } from 'react';

import Page from '@/components/Page';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Heading } from '@/components/Typography';
import Button from '@/components/Button';
import { axiosRubyClient } from '@/util/axios/axiosClient';

const PASSWORD = 'gravydevs123';

type ResultItem = {
  worker_id: number;
  error?: string;
};

type BypassResults = {
  succeeded: ResultItem[];
  failed: ResultItem[];
};

const I9Bypass: React.FC = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [workerIds, setWorkerIds] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<BypassResults | null>(null);
  const [error, setError] = useState('');

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === PASSWORD) {
      setAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const ids = workerIds
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id !== '');

    if (ids.length === 0) {
      setError('Please enter at least one worker ID');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosRubyClient.post('/api/admin/i9_bypass', {
        worker_ids: ids,
      });
      setResults(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <Page size="full" title="I-9 Bypass">
        <Card>
          <Card.Section>
            <form onSubmit={handlePasswordSubmit}>
              <Stack gap={16} vertical>
                <Heading>Enter Password</Heading>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    width: '300px',
                  }}
                />
                {passwordError && (
                  <Body style={{ color: 'red' }}>{passwordError}</Body>
                )}
                <Button type="submit" label="Submit" />
              </Stack>
            </form>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  return (
    <Page size="full" title="I-9 Bypass">
      <Card>
        <Card.Section>
          <form onSubmit={handleSubmit}>
            <Stack gap={16} vertical>
              <Heading>Bulk I-9 Bypass</Heading>
              <Body>
                Enter comma-separated worker IDs. This will set the i9_form
                onboarding step to completed and i9_status to approved.
              </Body>
              <textarea
                value={workerIds}
                onChange={(e) => setWorkerIds(e.target.value)}
                placeholder="e.g. 123, 456, 789"
                rows={5}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '100%',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                }}
              />
              {error && <Body style={{ color: 'red' }}>{error}</Body>}
              <Button
                type="submit"
                label={loading ? 'Processing...' : 'Bypass I-9'}
                disabled={loading}
              />
            </Stack>
          </form>
        </Card.Section>
      </Card>

      {results && (
        <Card style={{ marginTop: '16px' }}>
          <Card.Section>
            <Stack gap={16} vertical>
              <Heading>Results</Heading>
              {results.succeeded.length > 0 && (
                <div>
                  <Body style={{ fontWeight: 600, color: 'green' }}>
                    Succeeded ({results.succeeded.length}):
                  </Body>
                  <Body>
                    {results.succeeded.map((r) => r.worker_id).join(', ')}
                  </Body>
                </div>
              )}
              {results.failed.length > 0 && (
                <div>
                  <Body style={{ fontWeight: 600, color: 'red' }}>
                    Failed ({results.failed.length}):
                  </Body>
                  {results.failed.map((r, i) => (
                    <Body key={i}>
                      Worker {r.worker_id}: {r.error}
                    </Body>
                  ))}
                </div>
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}
    </Page>
  );
};

export default I9Bypass;
